import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store, persistor } from './redux/store';
import App from './App';
import trpc, { TrpcProvider } from 'lib/trpc';
import { httpBatchLink } from '@trpc/client';
import SuperJSON from 'superjson';
import { CalendarDay, CalendarTime } from 'further-ui/utils';
import { checkLocalStorage } from 'further-ui/utils';
import { LocalStorageError } from 'further-ui/components';
import LogRocket from 'logrocket';

SuperJSON.registerCustom<CalendarDay, string>(
  {
    isApplicable: (v): v is CalendarDay => v instanceof CalendarDay,
    serialize: (v) => v.toString(),
    deserialize: (v) => new CalendarDay(v),
  },
  'CalendarDay',
);

SuperJSON.registerCustom<CalendarTime, string>(
  {
    isApplicable: (v): v is CalendarTime => v instanceof CalendarTime,
    serialize: (v) => v.toString(),
    deserialize: (v) => new CalendarTime(v),
  },
  'CalendarTime',
);

import 'antd/dist/antd.min.css';

// Check localStorage availability before initializing the app
const localStorageCheck = checkLocalStorage();

if (!localStorageCheck.isAvailable) {
  const container = document.getElementById('root');
  if (container) {
    const root = createRoot(container);
    root.render(<LocalStorageError logoPath="assets/images/logo.webp" />);
  }
} else {
  const queryClient = new QueryClient();
  const trpcUrl = (() => {
    const configured = process.env.REACT_APP_TRPC_URL;
    if (typeof configured !== 'string' || !configured.length) return configured;

    const hostname = window.location.hostname;
    const isLocalhostHost =
      hostname === 'localhost' || hostname.endsWith('.localhost');
    if (!isLocalhostHost) return configured;

    try {
      const url = new URL(configured);
      const port = url.port || '4500';
      return `http://${hostname}:${port}/api/v2`;
    } catch {
      return configured;
    }
  })();

  const trpcClient = trpc.createClient({
    links: [
      httpBatchLink({
        url: `${trpcUrl}/`,
        transformer: SuperJSON,
        fetch: (url, options) =>
          window.fetch(url, { ...(options ?? {}), credentials: 'include' }),
        headers: async () => {
          return {
            ...(LogRocket.sessionURL && {
              'x-logrocket-url': LogRocket.sessionURL,
            }),
          };
        },
      }),
    ],
  });
  const container = document.getElementById('root');
  // @ts-expect-error allow undefined
  const root = createRoot(container);

  root.render(
    <TrpcProvider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <App />
          </PersistGate>
        </Provider>
      </QueryClientProvider>
    </TrpcProvider>,
  );
}
